import { useMutation } from '@apollo/client';
import * as icons from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { TooltipSquareIconButton } from 'components/common/icon/tooltip-icon-button';
import { FC, useEffect, useState } from 'react';
import {
  SyncLeadWithHighLevel,
  SyncLeadWithHighLevelVariables,
} from 'types/apollo/SyncLeadWithHighLevel';
import { SyncLeadWithHighLevelMutation } from './conversation.graphql';
import { LeadStatusChip } from './lead-status-chip';

interface SyncHighLevelDialogProps {
  fetchConversation: () => Promise<any>;
  campaignId: string;
  phone: string;
}

export const SyncHighLevelDialog: FC<SyncHighLevelDialogProps> = ({
  campaignId,
  fetchConversation,
  phone,
}) => {
  const [syncResult, setSyncResult] = useState<
    SyncLeadWithHighLevel['syncLeadWithHighLevel'] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [syncLeadWithHighLevel] = useMutation<
    SyncLeadWithHighLevel,
    SyncLeadWithHighLevelVariables
  >(SyncLeadWithHighLevelMutation);

  useEffect(() => {
    if (open) {
      if (loading) {
        return;
      }
      setLoading(true);
      setSyncResult(null);

      syncLeadWithHighLevel({
        variables: {
          input: {
            campaignId,
            leadPhoneNumber: phone,
          },
        },
      })
        .then((result) => {
          if (result.data) {
            setSyncResult(result.data.syncLeadWithHighLevel);
          }
        })
        .catch(() => {
          setOpen(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  const fetchConversationAndClose = async () => {
    await fetchConversation();
    setOpen(false);
  };

  return (
    <>
      <TooltipSquareIconButton tooltip="Sync with HighLevel" onClick={() => setOpen(true)}>
        <icons.SyncAlt />
      </TooltipSquareIconButton>

      <Dialog open={open} onClose={fetchConversationAndClose} maxWidth="sm" fullWidth>
        <DialogTitle>{loading ? 'Syncing' : 'Synced'} with High Level</DialogTitle>
        <DialogContent>
          {loading && (
            <Box sx={{ width: '100%', mb: 3 }}>
              <LinearProgress />
            </Box>
          )}

          {syncResult && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Contact ID: {syncResult.contactId}
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Changes:
              </Typography>

              {syncResult.changes.length > 0 ? (
                syncResult.changes.map((change) => (
                  <Box key={change.eventId} sx={{ mt: 2, pl: 2, borderLeft: '2px solid #ccc' }}>
                    {change.message}

                    <Box display="flex" alignItems="center" gap={1}>
                      <LeadStatusChip status={change.status} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Event ID: {change.eventId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Time: {change.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No changes were made
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={fetchConversationAndClose} variant="contained">
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
